import React, { useState } from 'react';
import { useStore } from '../../store/StoreContext';
import { useToast } from '../../hooks/useToast';
import ConfirmDialog from '../ui/ConfirmDialog';

// ── Crew-swap notification prompt (Jaylen 2026-07-08) ──────────────────
// Rendered ONCE in App.jsx. When StoreContext.saveJob detects a crew
// change on a LIVE job (dispatched / in_progress), it sets swapNotice and
// this dialog asks the dispatcher whether to text the affected LEADS.
// Confirm-first by design — the board never sends surprise SMS (same
// culture as Daily Dispatch). Messages go through /api/send-sms
// (RingCentral), one per recipient with a phone on file.
//
// Recipients (Jaylen's scope call): leads only —
//   • lead removed/replaced → "you're no longer on X"
//   • lead added/new       → "you're now leading X"
//   • same lead, crew size changed → "your crew for X changed"
export default function CrewSwapNotice() {
    const { swapNotice, clearSwapNotice, crew, clients } = useStore();
    const [sending, setSending] = useState(false);
    const { show } = useToast();

    if (!swapNotice) return null;

    const { job, prevLeaderId, newLeaderId } = swapNotice;
    const client = clients.find(c => c.id === job.client_id);
    const clientName = client?.name || 'a job';
    const when = [job.move_date, job.time].filter(Boolean).join(' at ') || 'the scheduled date';

    const byId = (id) => crew.find(c => c.id === id) || null;
    const recipients = [];
    if (prevLeaderId && prevLeaderId !== newLeaderId) {
        const m = byId(prevLeaderId);
        if (m) recipients.push({
            member: m,
            body: `BBM Dispatch: heads up — you're no longer on ${clientName} (${when}). Check your portal for your current jobs.`,
            label: `${m.name} (removed lead)`,
        });
    }
    if (newLeaderId && newLeaderId !== prevLeaderId) {
        const m = byId(newLeaderId);
        if (m) recipients.push({
            member: m,
            body: `BBM Dispatch: crew change — you're now leading ${clientName} (${when}). Check your portal for details.`,
            label: `${m.name} (new lead)`,
        });
    }
    if (recipients.length === 0 && newLeaderId) {
        // Same lead, crew composition changed.
        const m = byId(newLeaderId);
        if (m) recipients.push({
            member: m,
            body: `BBM Dispatch: your crew for ${clientName} (${when}) changed — now ${(job.crew_ids || []).length} crew. Check your portal.`,
            label: `${m.name} (lead — crew updated)`,
        });
    }

    const sendable = recipients.filter(r => r.member.phone);
    const phoneless = recipients.filter(r => !r.member.phone);

    const handleSend = async () => {
        setSending(true);
        let ok = 0, fail = 0;
        for (const r of sendable) {
            try {
                const res = await fetch('/api/send-sms', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ to: r.member.phone, body: r.body }),
                });
                if (res.ok) ok++; else fail++;
            } catch { fail++; }
        }
        setSending(false);
        clearSwapNotice();
        if (fail === 0) show(`Texted ${ok} lead${ok === 1 ? '' : 's'} about the change`);
        else show(`${ok} sent, ${fail} failed — try Quick Text for the rest`, 'error');
    };

    // Nothing to say (e.g. job had no leads at all) — auto-dismiss.
    if (recipients.length === 0) { clearSwapNotice(); return null; }

    return (
        <ConfirmDialog
            open
            title={`Text the crew about ${clientName}'s change?`}
            confirmLabel={sending ? 'Sending…' : sendable.length ? `Text ${sendable.length} lead${sendable.length === 1 ? '' : 's'}` : 'OK'}
            cancelLabel="Skip"
            onConfirm={sendable.length && !sending ? handleSend : clearSwapNotice}
            onCancel={() => { if (!sending) clearSwapNotice(); }}>
            <div className="space-y-2">
                <div>Crew changed on a live job — <span className="font-semibold">{clientName}</span> ({when}).</div>
                {sendable.map((r, i) => (
                    <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg p-2">
                        <div className="text-[11px] font-bold text-slate-500">{r.label}</div>
                        <div className="text-[12px] text-slate-700">{r.body}</div>
                    </div>
                ))}
                {phoneless.length > 0 && (
                    <div className="text-[11px] text-amber-700">
                        No phone on file for: {phoneless.map(r => r.member.name).join(', ')} — they won't be texted.
                    </div>
                )}
            </div>
        </ConfirmDialog>
    );
}
